import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ActivityIcon, AlertTriangleIcon, CheckCircleIcon, InfoIcon } from 'lucide-react';
interface Alert {
  id: string;
  time: string;
  level: string;
  message: string;
}
interface AlertsFeedProps {
  alerts: Alert[];
  phase: number;
  isRecomputing?: boolean;
}
export function AlertsFeed({
  alerts,
  phase,
  isRecomputing
}: AlertsFeedProps) {
  const getAlertIcon = (level: string) => {
    switch (level) {
      case 'warn':
        return <AlertTriangleIcon size={11} className="text-amber-500" data-id="element-114" />;
      case 'success':
        return <CheckCircleIcon size={11} className="text-emerald-500" data-id="element-115" />;
      case 'info':
      default:
        return <InfoIcon size={11} className="text-blue-500" data-id="element-116" />;
    }
  };
  const getAlertColor = (level: string) => {
    switch (level) {
      case 'warn':
        return 'text-amber-400';
      case 'success':
        return 'text-emerald-400';
      case 'info':
      default:
        return 'text-blue-400';
    }
  };
  return <div className={`bg-slate-900 border rounded p-3.5 h-full flex flex-col transition-colors ${isRecomputing ? 'border-emerald-500/50' : 'border-slate-800'}`} data-id="element-117">
      <div className="flex items-center justify-between mb-3 pt-2" data-id="element-118">
        <h3 className="text-base font-semibold text-slate-300 tracking-tight" data-id="element-119">
          Real-Time Alerts
        </h3>
        <ActivityIcon size={16} className="text-slate-600" data-id="element-120" />
      </div>

      <div className="flex-1 overflow-y-auto space-y-2 max-h-[280px]" data-id="element-121">
        <AnimatePresence mode="popLayout" data-id="element-122">
          {alerts.length === 0 && phase < 5 && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} className="text-[11px] text-slate-600 text-center py-6" data-id="element-123">
              Monitoring...
            </motion.div>}

          {alerts.map((alert, index) => <motion.div key={alert.id} initial={{
          opacity: 0,
          x: -10,
          scale: 0.98
        }} animate={{
          opacity: 1,
          x: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          x: 10,
          scale: 0.98
        }} transition={{
          delay: index * 0.08,
          duration: 0.2
        }} className="border border-slate-800 rounded p-2.5 bg-slate-900/50" data-id="element-124">
              <div className="flex items-start gap-2" data-id="element-125">
                <span className="text-[10px] text-slate-600 font-mono w-10 flex-shrink-0 mt-0.5" data-id="element-126">
                  {alert.time}
                </span>
                <span className="flex-shrink-0 mt-0.5" data-id="element-127">
                  {getAlertIcon(alert.level)}
                </span>
                <span className={`text-[11.5px] ${getAlertColor(alert.level)} flex-1 leading-snug`} data-id="element-128">
                  {alert.message}
                </span>
              </div>
            </motion.div>)}
        </AnimatePresence>
      </div>

      {phase >= 5 && alerts.length > 0 && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 0.3,
      duration: 0.2
    }} className="mt-3 pt-3 border-t border-slate-800" data-id="element-129">
          <div className="flex items-center gap-2 text-[10px] text-slate-500" data-id="element-130">
            <span className={`inline-block h-1 w-1 rounded-full animate-pulse ${isRecomputing ? 'bg-emerald-400' : 'bg-blue-500'}`} data-id="element-131" />
            <span className="font-mono" data-id="element-132">{alerts.length} alerts</span>
          </div>
        </motion.div>}
    </div>;
}